import { Item } from "@dto/records";
import { TW_CurrencyCell, TW_CurrencyCell_editing } from "@utils/twClasses";
import { displayCurrency, valueStringOrDefault } from "@utils/types";
import { useEffect, useState } from "react";
import { useModelStore, useUserStore } from "src/store";
import { AbstractCell } from "./AbstractCell";
import { CellProps } from "./GridCell";
import { EditRightAccess, hasGlobalRightAccess, ObjectRightType, ViewRightType } from "@dto/Users";


export function CurrencyCell({ pathKey, field, item, position, updateValue, isEditing, inputRef, blurTriggered }: CellProps) {

    const userRole = useModelStore(state => state.userRole);
    const user = useUserStore(state => state.user);
    const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
    const selectedView = useModelStore(state => state.selectedView);
    const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
    const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
    const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
    const readOnly = item.readOnly || !hasEditAccess ;

    let currency = "€"
    let symbolBefore = false;
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    if (format) {
        currency = format.get("symbol") || "€";
        symbolBefore = (format.get("before") === true) ? true : false;
    }

    const curRegex = /^-?\d*\.?\d*$/;

    const [text, setText] = useState(valueStringOrDefault(item?.value, ""));
    const wsColor = useModelStore(state => state.wsColor);
    const [lastBlurTriggered, setLastBlurTriggered] = useState<number | null>(null);

    useEffect(() => {
        if (item?.value !== text) {
            setText(valueStringOrDefault(item?.value, ""));
        }
    }, [item]);

    useEffect(() => {
        const newValue = textToValue(text);
        if (isEditing && lastBlurTriggered && blurTriggered && lastBlurTriggered !== blurTriggered && item?.value !== newValue) {
            updateValue(item, newValue);
        }
        setLastBlurTriggered(blurTriggered);
    }, [blurTriggered]);

    const updateLocalValue = (item: Item, value: any) => {
        setText(value || "");
        updateValue(item, value);
    }

    const textToValue = (text: string | null): any => {
        if (!text || text.length === 0) {
            return null;
        }
        if (text === "-") {
            return 0;
        }
        if (text === "Error") {
            return "Error";
        }
        return parseFloat(text);
    }

    function isTextValid(value: string | null) {
        if (!value || value.length === 0) {
            return true;
        }
        return curRegex.test(value);
    }


    const valueToText = (): string => {
        return displayCurrency(textToValue(text), format);
    }

    const renderReadOnlyContent = () => {
        const valueStr = valueToText();
        return (
            <div className={TW_CurrencyCell + (readOnly ? " cursor-not-allowed" : "")}>
                {valueStr}
            </div>
        )
    }

    const renderEditingContent = (
        onTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
        onEnterKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void,
        inputRef: React.RefObject<HTMLInputElement>) => {
        return (<>
            {symbolBefore ? <div>{currency}</div> : null}
            <input
                className={TW_CurrencyCell_editing}
                type="text"
                value={text}
                onChange={onTextChange}
                onKeyDown={onEnterKeyPress}
                autoFocus={true}
                style={{ borderColor: wsColor }}
                ref={inputRef}/>
            {!symbolBefore ? <div>{currency}</div> : null}
        </>)
    }

    return <AbstractCell
        item={item}
        position={position}
        updateValue={updateLocalValue}
        renderReadOnlyContent={renderReadOnlyContent}
        renderEditingContent={renderEditingContent}
        textToValue={textToValue}
        isTextValid={isTextValid}
        text={text}
        setText={setText}
        isEditing={isEditing}
        inputRef={inputRef}
        readOnly={readOnly}
    />
}
